<template>
  <div class="panel-card full-height" :class="{ loading: loading }">
    <div class="loading-overlay" v-if="loading"></div>
    <div class="page-header">
      <div class="page-header-main">
        <h3 class="page-header-title">{{ pageTitle }}</h3>
        <span class="page-header-sub">共 {{ pagination.total }} 条记录</span>
      </div>
      <div class="page-header-actions">
        <el-button
          type="primary"
          size="small"
          class="refresh-btn"
          :loading="syncingCustomers"
          @click="refreshCustomerTable"
        >
          {{ refreshButtonText }}
        </el-button>
        <el-button
          size="small"
          class="export-btn"
          :loading="exporting"
          @click="openExportDialog"
        >
          导出Excel
        </el-button>
        <span class="ledger-refresh-text">自动刷新: {{ ledgerRefreshInterval / 1000 }}s</span>
        <el-button
          v-if="isMobileViewport"
          size="small"
          class="mobile-filter-toggle"
          @click="toggleMobileFilters"
        >
          {{ mobileFiltersExpanded ? "收起筛选" : "展开筛选" }}
        </el-button>
        <div v-if="!isMobileViewport || mobileFiltersExpanded" class="filter-controls">
          <div class="filter-row filter-row-name">
            <span class="filter-label">客户名称</span>
            <el-input
              v-model="filters.customer_name"
              size="small"
              clearable
              class="name-filter"
              placeholder="请输入客户名称"
              @keyup.enter.native="handleFilterSearch"
              @clear="handleFilterSearch"
            />
            <el-button
              type="primary"
              size="small"
              class="filter-query-btn"
              @click="handleFilterSearch"
            >
              查询
            </el-button>
          </div>
          <div class="filter-row">
            <span class="filter-label">对接销售</span>
            <el-select
              v-model="filters.sales_name"
              size="small"
              clearable
              class="sales-filter"
              placeholder="全部"
              @change="handleSalesFilterChange"
            >
              <el-option label="全部" value=""></el-option>
              <el-option
                v-for="name in salesOptions"
                :key="name"
                :label="name"
                :value="name"
              ></el-option>
            </el-select>
          </div>
          <div v-if="!isLeadMode" class="filter-row">
            <span class="filter-label">AI介入</span>
            <el-select
              v-model="filters.ai_intervention_range"
              size="small"
              class="ai-filter"
              @change="handleAiInterventionFilterChange"
            >
              <el-option label="全部" value="all"></el-option>
              <el-option label="AI未介入对话" value="none"></el-option>
              <el-option label="AI介入对话" value="any"></el-option>
              <el-option label="AI介入对话10次及以上" value="gte10"></el-option>
              <el-option label="AI介入对话30次以内" value="lte30"></el-option>
              <el-option label="AI介入对话超过30次" value="gt30"></el-option>
            </el-select>
          </div>
          <div v-if="!isLeadMode" class="filter-row">
            <span class="filter-label">成单</span>
            <el-select
              v-model="filters.deal_status"
              size="small"
              class="deal-filter"
              @change="handleDealStatusFilterChange"
            >
              <el-option label="全部" value="all"></el-option>
              <el-option label="是" value="yes"></el-option>
              <el-option label="否" value="no"></el-option>
            </el-select>
          </div>
        </div>
      </div>
    </div>

    <div class="table-shell" style="margin-top: 8px;">
      <div v-if="isMobileViewport" class="mobile-ledger-list">
        <div class="mobile-ledger-head">
          <span>客户微信</span>
          <span>对接销售</span>
          <span>客户标签</span>
          <span>操作</span>
        </div>
        <div v-if="!ledger.length" class="conv-empty">暂无客户记录</div>
        <div v-for="row in ledger" v-else :key="row.id" class="mobile-ledger-item">
          <div class="mobile-ledger-field mobile-ledger-field--wechat">
            <div class="mobile-wechat-cell">
              <button
                class="mobile-wechat-name customer-name-link"
                :disabled="loading || !row.id"
                @click="viewConversation(row)"
              >
                {{ row.customer_wechat_name || "-" }}
              </button>
              <div class="mobile-wechat-id">{{ row.wechat_id || "-" }}</div>
            </div>
          </div>
          <div class="mobile-ledger-field mobile-ledger-field--sales">
            <span class="mobile-sales-text">{{ row.sales_name || "-" }}</span>
          </div>
          <div class="mobile-ledger-field mobile-ledger-field--tags">
            <div class="mobile-ledger-tag-wrap" v-if="resolveCustomerTags(row).length">
              <el-tag
                v-for="tag in resolveCustomerTags(row)"
                :key="`mobile-customer-tag-${row.id}-${tag}`"
                size="mini"
                :type="getCustomerTagType(tag)"
                effect="plain"
              >
                {{ tag }}
              </el-tag>
            </div>
            <span v-else class="mobile-ledger-tag-empty">-</span>
          </div>
          <div class="mobile-ledger-field mobile-ledger-field--actions">
            <div class="operation-cell mobile">
              <button class="btn s xs" @click="viewConversation(row)" :disabled="loading">查看对话记录</button>
              <button
                v-if="!isLeadMode"
                class="btn w xs action-gap"
                @click="viewSummary(row)"
                :disabled="loading"
              >
                聊天总结
              </button>
              <button
                v-if="!isLeadMode"
                v-show="canManageAiSwitch"
                :class="['btn', 'xs', 'action-gap', isCustomerAiEnabled(row) ? 'w' : 'p']"
                :disabled="loading || !!aiSwitchLoadingMap[row.id]"
                @click="toggleCustomerAi(row)"
              >
                {{ isCustomerAiEnabled(row) ? "关闭AI" : "开启AI" }}
              </button>
            </div>
          </div>
          <div class="mobile-ledger-metrics">
            <div class="mobile-ledger-metric">
              <span class="mobile-ledger-metric-label">对话轮数</span>
              <span class="mobile-ledger-metric-value">{{ Number(row.dialogue_rounds || 0) }}</span>
            </div>
            <div v-if="!isLeadMode" class="mobile-ledger-metric">
              <span class="mobile-ledger-metric-label">AI介入次数</span>
              <span class="mobile-ledger-metric-value">{{ Number(row.ai_intervention_count || 0) }}</span>
            </div>
            <div v-if="!isLeadMode" class="mobile-ledger-metric">
              <span class="mobile-ledger-metric-label">成单</span>
              <span class="mobile-ledger-metric-value">{{ getDealStatusLabel(row) }}</span>
            </div>
            <div class="mobile-ledger-metric">
              <span class="mobile-ledger-metric-label">付款码</span>
              <span class="mobile-ledger-metric-value">{{ getPaymentCodeSentLabel(row) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="overflow-x-auto">
        <el-table
          class="customer-ledger-table"
          :data="ledger"
          :fit="true"
          size="mini"
          style="width: 100%"
          stripe
          height="calc(100% - 40px)"
        >
          <el-table-column type="index" label="序号" width="56"></el-table-column>
          <el-table-column label="客户微信名" min-width="118" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <button
                class="customer-name-link"
                :disabled="loading || !row.id"
                @click="viewConversation(row)"
              >
                {{ row.customer_wechat_name || "-" }}
              </button>
            </template>
          </el-table-column>
          <el-table-column prop="sales_name" label="对接销售" width="104" show-overflow-tooltip></el-table-column>
          <el-table-column v-if="!isLeadMode" label="成单" width="72" align="center">
            <template slot-scope="{ row }">
              {{ getDealStatusLabel(row) }}
            </template>
          </el-table-column>
          <el-table-column label="客户标签" min-width="152">
            <template slot-scope="{ row }">
              <div class="customer-tags-cell" v-if="resolveCustomerTags(row).length">
                <el-tag
                  v-for="tag in resolveCustomerTags(row)"
                  :key="`customer-tag-${row.id}-${tag}`"
                  size="mini"
                  :type="getCustomerTagType(tag)"
                  effect="plain"
                >
                  {{ tag }}
                </el-tag>
              </div>
              <span v-else class="customer-tag-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="dialogue_rounds" label="轮数" width="72" align="center"></el-table-column>
          <el-table-column v-if="!isLeadMode" prop="ai_intervention_count" label="AI次数" width="78" align="center">
            <template slot-scope="{ row }">
              {{ Number(row.ai_intervention_count || 0) }}
            </template>
          </el-table-column>
          <el-table-column v-if="!isLeadMode" label="成单" width="68" align="center">
            <template slot-scope="{ row }">
              {{ getDealStatusLabel(row) }}
            </template>
          </el-table-column>
          <el-table-column prop="first_chat_time" label="首次聊天时间" width="148" show-overflow-tooltip></el-table-column>
          <el-table-column prop="ai_last_chat_time" label="AI最后对话时间" width="148" show-overflow-tooltip></el-table-column>
          <el-table-column label="付款码" width="84" align="center">
            <template slot-scope="{ row }">
              {{ getPaymentCodeSentLabel(row) }}
            </template>
          </el-table-column>
          <el-table-column label="成交概率" width="148">
            <template slot-scope="{ row }">
              <el-tooltip
                effect="dark"
                placement="top"
                :content="getDealProbabilityReasonText(row)"
                :open-delay="120"
              >
                <div class="deal-probability-cell">
                  <el-tag :type="getDealProbabilityTagType(row)" size="small">
                    {{ getDealProbabilityLabel(row) }}
                  </el-tag>
                  <span class="deal-probability-score">{{ getDealProbabilityScore(row) }}%</span>
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="操作" :width="operationColumnWidth">
            <template slot-scope="{ row }">
              <div class="operation-cell">
                <button class="btn s xs" title="查看对话记录" @click="viewConversation(row)" :disabled="loading">对话</button>
                <button
                  v-if="!isLeadMode"
                  class="btn w xs action-gap"
                  title="查看聊天总结"
                  @click="viewSummary(row)"
                  :disabled="loading"
                >
                  总结
                </button>
                <button
                  v-if="!isLeadMode"
                  v-show="canManageAiSwitch"
                  :class="['btn', 'xs', 'action-gap', isCustomerAiEnabled(row) ? 'w' : 'p']"
                  title="切换AI回复状态"
                  :disabled="loading || !!aiSwitchLoadingMap[row.id]"
                  @click="toggleCustomerAi(row)"
                >
                  {{ isCustomerAiEnabled(row) ? "关AI" : "开AI" }}
                </button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="微信ID" min-width="128" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row.wechat_id || "-" }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <div class="pagination-container" v-if="pagination.total > 0">
      <el-pagination
        :current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog
      title="导出字段"
      :visible.sync="exportDialogVisible"
      width="560px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="export-dialog-body">
        <div class="export-dialog-tip">
          将按当前筛选条件导出全部结果，勾选需要导出的字段。
        </div>
        <el-checkbox-group v-model="exportSelectedFields" class="export-field-grid">
          <el-checkbox
            v-for="field in exportFieldOptions"
            :key="field.key"
            :label="field.key"
          >
            {{ field.label }}
          </el-checkbox>
        </el-checkbox-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="exportDialogVisible = false">取消</el-button>
        <el-button size="small" @click="selectAllExportFields">全选</el-button>
        <el-button size="small" @click="resetExportFields">重置</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="exporting"
          :disabled="!exportSelectedFields.length"
          @click="confirmExportExcel"
        >
          开始导出
        </el-button>
      </span>
    </el-dialog>

    <div v-if="chatVisible" class="chat-modal" @click.self="closeChat">
      <div class="chat-card">
        <div class="chat-head">
          <h3>对话记录 · {{ activeRow.customer_wechat_name || "-" }} / {{ activeRow.sales_name || "-" }}</h3>
          <div class="chat-head-actions">
            <span class="chat-refresh-text">自动刷新: {{ chatRefreshInterval / 1000 }}s</span>
            <el-button
              type="primary"
              size="mini"
              class="chat-refresh-btn"
              :loading="chatRefreshing"
              :disabled="chatLoading"
              @click="refreshConversation"
            >
              刷新
            </el-button>
            <button class="btn w xs" @click="closeChat" :disabled="chatLoading">关闭</button>
          </div>
        </div>
        <el-tabs v-model="conversationTab" class="conversation-tabs">
          <el-tab-pane label="全部聊天" name="all"></el-tab-pane>
          <el-tab-pane label="销售聊天" name="sales"></el-tab-pane>
          <el-tab-pane label="AI聊天" name="ai"></el-tab-pane>
        </el-tabs>
        <div class="chat-list">
          <div v-if="chatLoading" class="conv-empty">正在加载对话记录...</div>
          <div v-else-if="!filteredConversation.length" class="conv-empty">当前筛选暂无对话记录</div>
          <div
            v-for="(item, idx) in filteredConversation"
            v-else
            :key="idx"
            class="chat-item"
            :class="resolveSpeakerClass(item)"
          >
            <div class="chat-bubble">
              <small class="chat-meta">
                {{ resolveSpeakerLabel(item) }}
                <template v-if="item.sender_name"> · {{ item.sender_name }}</template>
                · {{ item.time || "-" }}
                <span
                  v-if="shouldShowConversationAiPushStatus(item)"
                  class="ai-push-status"
                  :class="getConversationAiPushStatusClass(item)"
                >
                  {{ getConversationAiPushStatusText(item) }}
                </span>
              </small>
              <template v-if="isConversationImage(item)">
                <a
                  v-if="canPreviewConversationImage(item)"
                  class="conversation-image-link"
                  :href="getConversationImageUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    class="conversation-image"
                    :src="getConversationImageUrl(item)"
                    alt="聊天图片"
                    @error="handleConversationImageError(item)"
                  />
                </a>
                <span v-else class="conversation-muted">{{ getConversationImageUnavailableText(item) }}</span>
              </template>
              <template v-else-if="isConversationVoice(item)">
                <audio
                  v-if="getConversationVoiceUrl(item)"
                  class="conversation-audio"
                  controls
                  preload="none"
                  :src="getConversationVoiceUrl(item)"
                ></audio>
                <span v-else class="conversation-muted">
                  语音消息（暂无可播放链接）
                  <template v-if="getConversationVoiceDuration(item)">
                    · 时长 {{ getConversationVoiceDuration(item) }}s
                  </template>
                </span>
              </template>
              <template v-else-if="isConversationFile(item)">
                <a
                  v-if="getConversationFileUrl(item)"
                  class="conversation-file-link"
                  :href="getConversationFileUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ getConversationFileName(item) || "点击查看文件" }}
                </a>
                <span v-else class="conversation-muted">
                  {{ getConversationFileName(item) || "文件消息（暂无可访问链接）" }}
                </span>
              </template>
              <template v-else-if="isConversationVideo(item)">
                <video
                  v-if="getConversationVideoUrl(item)"
                  class="conversation-video"
                  controls
                  preload="metadata"
                  :src="getConversationVideoUrl(item)"
                ></video>
                <span v-else class="conversation-muted">视频消息（暂无可播放链接）</span>
              </template>
              <template v-else-if="isConversationAppCard(item)">
                <div class="conversation-app-card">
                  <div class="conversation-app-header">
                    <img
                      v-if="getConversationAppIconUrl(item)"
                      class="conversation-app-icon"
                      :src="getConversationAppIconUrl(item)"
                      alt="应用图标"
                    />
                    <span class="conversation-app-name">
                      {{ getConversationAppName(item) || getConversationMessageTypeLabelByCode(item && item.msg_type) }}
                    </span>
                  </div>
                  <div class="conversation-app-title">
                    {{ getConversationAppTitle(item) || "应用消息" }}
                  </div>
                  <div v-if="getConversationAppDesc(item)" class="conversation-app-desc">
                    {{ getConversationAppDesc(item) }}
                  </div>
                  <a
                    v-if="getConversationAppUrl(item)"
                    class="conversation-file-link"
                    :href="getConversationAppUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    打开链接
                  </a>
                  <div v-else-if="getConversationAppPath(item)" class="conversation-muted">
                    {{ getConversationAppPath(item) }}
                  </div>
                </div>
              </template>
              <template v-else-if="isConversationCallRecord(item)">
                <div class="conversation-call-record">
                  <span class="conversation-call-record-title">{{ getConversationCallRecordText(item) }}</span>
                  <span v-if="getConversationCallDuration(item)" class="conversation-call-record-sub">
                    时长：{{ getConversationCallDuration(item) }}
                  </span>
                </div>
              </template>
              <template v-else-if="isConversationReadReceipt(item)">
                <div class="conversation-call-record">
                  <span class="conversation-call-record-title">{{ getConversationReadReceiptText(item) }}</span>
                </div>
              </template>
              <template v-else-if="isConversationRevoke(item)">
                <div class="conversation-revoke">
                  <span class="conversation-revoke-title">
                    消息已撤回
                    <template v-if="getConversationRevokeOriginalTypeLabel(item)">
                      （原消息：{{ getConversationRevokeOriginalTypeLabel(item) }}）
                    </template>
                  </span>
                  <span v-if="getConversationRevokeInfo(item)" class="conversation-revoke-sub">
                    {{ getConversationRevokeInfo(item) }}
                  </span>
                  <span v-if="getConversationRevokeMsgId(item)" class="conversation-revoke-sub">
                    原消息ID：{{ getConversationRevokeMsgId(item) }}
                  </span>
                </div>
              </template>
              <template v-else-if="isConversationBase64RawMessage(item)">
                <div class="conversation-call-record">
                  <span class="conversation-call-record-title">{{ getConversationBase64RawText(item) }}</span>
                  <span v-if="getConversationBase64RawLength(item)" class="conversation-call-record-sub">
                    原始数据长度：{{ getConversationBase64RawLength(item) }}
                  </span>
                </div>
              </template>
              <template v-else>
                {{ getConversationPlainText(item) }}
              </template>
            </div>
          </div>
        </div>
        <div v-if="!isLeadMode" class="chat-composer">
          <el-input
            v-model="chatComposerText"
            type="textarea"
            :rows="3"
            resize="none"
            maxlength="1000"
            show-word-limit
            :disabled="chatLoading || chatSending"
            placeholder="输入要发送给客户的文本消息..."
          />
          <div class="chat-composer-actions">
            <el-upload
              ref="chatImageUpload"
              class="chat-image-upload"
              action="#"
              :show-file-list="false"
              :auto-upload="false"
              :limit="1"
              accept="image/*"
              :disabled="chatLoading || chatSending"
              :on-change="handleConversationImageChange"
              :on-exceed="handleConversationImageExceed"
            >
              <el-button size="mini" :disabled="chatLoading || chatSending">上传图片</el-button>
            </el-upload>
            <span v-if="chatComposerImageName" class="chat-image-name">{{ chatComposerImageName }}</span>
            <el-button
              v-if="chatComposerImage"
              size="mini"
              type="text"
              class="chat-image-remove"
              :disabled="chatLoading || chatSending"
              @click="clearConversationImage"
            >
              清除图片
            </el-button>
            <el-button
              type="primary"
              size="mini"
              :loading="chatSending"
              :disabled="chatLoading || chatSending"
              @click="sendConversationMessage"
            >
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLeadMode && summaryVisible" class="chat-modal" @click.self="closeSummary">
      <div class="chat-card summary-card">
        <div class="chat-head">
          <h3>
            聊天总结 · {{ summaryRow.customer_wechat_name || "-" }} / {{ summaryRow.sales_name || "-" }}
            <span class="summary-source">({{ summarySourceLabel }})</span>
          </h3>
          <button class="btn w xs" @click="closeSummary" :disabled="summaryLoading">关闭</button>
        </div>
        <div class="chat-list summary-body">
          <div v-if="summaryLoading" class="conv-empty">正在生成聊天总结...</div>
          <template v-else>
            <div class="summary-block">
              <h4>总结信息</h4>
              <p>{{ summaryResult.brief || "暂无总结内容" }}</p>
              <div v-if="summaryCoreInfo.length" class="summary-kv" style="margin-top: 10px;">
                <div v-for="(item, idx) in summaryCoreInfo" :key="`k-${idx}`" class="summary-kv-row">
                  <span class="summary-k">{{ item.label || "-" }}</span>
                  <span class="summary-v">{{ item.value || "-" }}</span>
                </div>
              </div>
              <div v-else class="conv-empty">暂无可提炼的信息</div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { customerApi } from "../../api";

export default {
  props: {
    leadMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ledger: [],
      chatVisible: false,
      isMobileViewport: false,
      viewportWidth: 1440,
      mobileFiltersExpanded: false,
      chatLoading: false,
      activeRow: {},
      conversationTab: "all",
      conversation: [],
      conversationImageLoadFailedMap: {},
      chatRefreshInterval: 8000,
      chatRefreshTimerId: null,
      chatRefreshing: false,
      conversationLoadToken: 0,
      chatSending: false,
      chatComposerText: "",
      chatComposerImage: null,
      chatComposerImageName: "",
      summaryVisible: false,
      summaryLoading: false,
      summaryRow: {},
      summaryResult: {
        brief: "",
        key_info: [],
        highlights: [],
        deal_advice: [],
        source: "",
        stats: {}
      },
      aiSwitchRefreshInterval: 2000,
      aiSwitchRefreshTimerId: null,
      aiSwitchRefreshLoading: false,
      aiSwitchRefreshPending: false,
      ledgerRefreshInterval: 30000,
      ledgerRefreshTimerId: null,
      syncingCustomers: false,
      exportDialogVisible: false,
      exporting: false,
      exportSelectedFields: [],
      aiSwitchLoadingMap: {},
      loading: false,
      lastLedgerFetchAt: 0,
      lastLedgerFetchKey: "",
      salesOptions: [],
      filters: {
        customer_name: "",
        sales_name: "",
        ai_intervention_range: "any",
        deal_status: "all"
      },
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
      }
    };
  },
  created() {
    this.updateViewportMode();
    this.syncFiltersFromRoute();
    this.resetExportFields();
    this.loadLedger();
    this.startLedgerAutoRefresh();
    this.startAiSwitchStatusAutoRefresh();
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.updateViewportMode, { passive: true });
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.updateViewportMode);
    }
    this.stopConversationAutoRefresh();
    this.stopLedgerAutoRefresh();
    this.stopAiSwitchStatusAutoRefresh();
  },
  methods: {
    updateViewportMode() {
      if (typeof window === "undefined") return;
      this.viewportWidth = Number(window.innerWidth || 0);
      const nextIsMobile = this.viewportWidth <= 900;
      const prevIsMobile = this.isMobileViewport;
      this.isMobileViewport = nextIsMobile;
      if (nextIsMobile && !prevIsMobile) {
        this.mobileFiltersExpanded = false;
      }
      if (!nextIsMobile) {
        this.mobileFiltersExpanded = true;
      }
    },
    toggleMobileFilters() {
      if (!this.isMobileViewport) return;
      this.mobileFiltersExpanded = !this.mobileFiltersExpanded;
    },
    syncFiltersFromRoute() {
      const routeSalesName = String((this.$route.query && this.$route.query.sales_name) || "").trim();
      const routeAiInterventionRange = String((this.$route.query && this.$route.query.ai_intervention_range) || "").trim();
      const routeDealStatus = String((this.$route.query && this.$route.query.deal_status) || "").trim().toLowerCase();
      this.filters.sales_name = routeSalesName;
      this.filters.ai_intervention_range = ["all", "none", "any", "gte10", "lte30", "gt30"].includes(routeAiInterventionRange)
        ? routeAiInterventionRange
        : (this.isLeadMode ? "all" : "any");
      this.filters.deal_status = ["all", "yes", "no"].includes(routeDealStatus)
        ? routeDealStatus
        : "all";
    },
    parseIsDealFlag(value) {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric === 1;
      }
      const text = String(value == null ? "" : value).trim().toLowerCase();
      return ["1", "true", "yes", "y", "是", "已委托"].includes(text);
    },
    applyDealStatusFallback(rows) {
      const list = Array.isArray(rows) ? rows : [];
      if (this.isLeadMode) {
        return list;
      }
      const dealStatus = ["all", "yes", "no"].includes(String(this.filters.deal_status || "").toLowerCase())
        ? String(this.filters.deal_status || "all").toLowerCase()
        : "all";
      if (dealStatus === "all") {
        return list;
      }
      const expectDeal = dealStatus === "yes";
      return list.filter((row) => this.parseIsDealFlag(row && row.is_deal) === expectDeal);
    },
    async loadLedger(options = {}) {
      const force = !!(options && options.force);
      const fetchKey = [
        this.isLeadMode ? 1 : 0,
        this.pagination.current_page,
        this.pagination.per_page,
        this.filters.customer_name || "",
        this.filters.sales_name || "",
        this.isLeadMode ? "all" : (this.filters.ai_intervention_range || "any"),
        this.isLeadMode ? "all" : (this.filters.deal_status || "all")
      ].join("|");
      const now = Date.now();
      if (!force && this.loading) return;
      if (!force && fetchKey === this.lastLedgerFetchKey && now - this.lastLedgerFetchAt < 800) {
        return;
      }
      this.loading = true;
      try {
        const fetchFn = this.isLeadMode ? customerApi.getLeadLedger : customerApi.getCustomerLedger;
        const data = await fetchFn({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          realtime: 1,
          customer_name: this.filters.customer_name || undefined,
          sales_name: this.filters.sales_name || undefined,
          ai_intervention_range: this.isLeadMode ? undefined : (this.filters.ai_intervention_range || "any"),
          deal_status: this.isLeadMode ? undefined : (this.filters.deal_status || "all")
        });

        const rows = Array.isArray(data) ? data : data.items || [];
        this.ledger = this.applyDealStatusFallback(rows);
        this.salesOptions = data && data.filters && Array.isArray(data.filters.sales_names)
          ? data.filters.sales_names
          : this.salesOptions;
        if (data.pagination) {
          this.pagination = {
            ...this.pagination,
            ...data.pagination
          };
        } else {
          this.pagination.total = this.ledger.length;
        }
        this.refreshAiSwitchStatuses({ silent: true });
        this.lastLedgerFetchKey = fetchKey;
        this.lastLedgerFetchAt = Date.now();
      } catch (e) {
        this.ledger = [];
        this.pagination.total = 0;
        this.$message.error(this.isLeadMode ? "加载线索列表失败" : "加载客户列表失败");
      } finally {
        this.loading = false;
      }
    },
    handleSizeChange(size) {
      this.pagination.per_page = size;
      this.pagination.current_page = 1;
      this.loadLedger();
    },
    handleCurrentChange(current) {
      this.pagination.current_page = current;
      this.loadLedger();
    },
    handleFilterSearch() {
      this.pagination.current_page = 1;
      this.loadLedger();
    },
    handleSalesFilterChange(value) {
      this.filters.sales_name = value || "";
      this.pagination.current_page = 1;
      this.loadLedger();
    },
    handleAiInterventionFilterChange(value) {
      this.filters.ai_intervention_range = ["all", "none", "any", "gte10", "lte30", "gt30"].includes(value) ? value : "any";
      this.pagination.current_page = 1;
      this.loadLedger();
    },
    handleDealStatusFilterChange(value) {
      this.filters.deal_status = ["all", "yes", "no"].includes(String(value || "").toLowerCase())
        ? String(value || "").toLowerCase()
        : "all";
      this.pagination.current_page = 1;
      this.loadLedger();
    },
    openExportDialog() {
      if (!this.exportSelectedFields.length) {
        this.resetExportFields();
      }
      this.exportDialogVisible = true;
    },
    resetExportFields() {
      this.exportSelectedFields = this.exportFieldOptions.map((item) => item.key);
    },
    selectAllExportFields() {
      this.exportSelectedFields = this.exportFieldOptions.map((item) => item.key);
    },
    buildLedgerQueryParams(overrides = {}) {
      return {
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
        realtime: 1,
        customer_name: this.filters.customer_name || undefined,
        sales_name: this.filters.sales_name || undefined,
        ai_intervention_range: this.isLeadMode ? undefined : (this.filters.ai_intervention_range || "any"),
        deal_status: this.isLeadMode ? undefined : (this.filters.deal_status || "all"),
        ...overrides
      };
    },
    async fetchAllLedgerRowsForExport() {
      const fetchFn = this.isLeadMode ? customerApi.getLeadLedger : customerApi.getCustomerLedger;
      const perPage = 100;
      const firstData = await fetchFn(this.buildLedgerQueryParams({
        page: 1,
        per_page: perPage
      }));

      const firstItemsRaw = Array.isArray(firstData) ? firstData : (Array.isArray(firstData.items) ? firstData.items : []);
      const firstItems = this.applyDealStatusFallback(firstItemsRaw);
      const firstPagination = firstData && firstData.pagination ? firstData.pagination : {};
      const totalPages = Math.max(1, Number(firstPagination.last_page || 1));
      if (totalPages <= 1) {
        return firstItems;
      }

      const rows = firstItems.slice();
      const concurrency = 3;
      const pages = [];
      for (let page = 2; page <= totalPages; page += 1) {
        pages.push(page);
      }

      for (let index = 0; index < pages.length; index += concurrency) {
        const batchPages = pages.slice(index, index + concurrency);
        const batchResults = await Promise.all(
          batchPages.map((page) => fetchFn(this.buildLedgerQueryParams({
            page,
            per_page: perPage
          })))
        );
        batchResults.forEach((data) => {
          const itemsRaw = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
          const items = this.applyDealStatusFallback(itemsRaw);
          rows.push(...items);
        });
      }

      return rows;
    },
    getExportFieldValue(row, fieldKey, index) {
      switch (fieldKey) {
        case "index":
          return index + 1;
        case "customer_wechat_name":
          return row && row.customer_wechat_name ? row.customer_wechat_name : "-";
        case "sales_name":
          return row && row.sales_name ? row.sales_name : "-";
        case "is_deal":
          return this.getDealStatusLabel(row);
        case "customer_tags": {
          const tags = this.resolveCustomerTags(row);
          return tags.length ? tags.join("、") : "-";
        }
        case "dialogue_rounds":
          return Number(row && row.dialogue_rounds ? row.dialogue_rounds : 0);
        case "ai_intervention_count":
          return Number(row && row.ai_intervention_count ? row.ai_intervention_count : 0);
        case "first_chat_time":
          return row && row.first_chat_time ? row.first_chat_time : "-";
        case "ai_last_chat_time":
          return row && row.ai_last_chat_time ? row.ai_last_chat_time : "-";
        case "payment_code_sent":
          return this.getPaymentCodeSentLabel(row);
        case "deal_probability":
          return `${this.getDealProbabilityLabel(row)} ${this.getDealProbabilityScore(row)}%`;
        case "wechat_id":
          return row && row.wechat_id ? row.wechat_id : "-";
        default:
          return row && row[fieldKey] != null ? row[fieldKey] : "-";
      }
    },
    escapeExportHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    buildExportTableHtml(columns, rows) {
      const headHtml = columns
        .map((column) => `<th>${this.escapeExportHtml(column.label)}</th>`)
        .join("");
      const bodyHtml = rows
        .map((row, index) => {
          const cellHtml = columns
            .map((column) => `<td>${this.escapeExportHtml(this.getExportFieldValue(row, column.key, index))}</td>`)
            .join("");
          return `<tr>${cellHtml}</tr>`;
        })
        .join("");

      return [
        "<html>",
        "<head>",
        "<meta charset=\"utf-8\">",
        "<style>table{border-collapse:collapse;font-size:12px;}th,td{border:1px solid #dcdfe6;padding:8px 10px;text-align:left;white-space:nowrap;}th{background:#f5f7fa;font-weight:600;}</style>",
        "</head>",
        "<body>",
        `<table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`,
        "</body>",
        "</html>"
      ].join("");
    },
    downloadExcelFile(content, filename) {
      const blob = new Blob([`\ufeff${content}`], {
        type: "application/vnd.ms-excel;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    getExportFileName() {
      const date = new Date();
      const pad = (value) => String(value).padStart(2, "0");
      const timestamp = [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate())
      ].join("") + "_" + [
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join("");
      return `${this.isLeadMode ? "线索列表" : "客户列表"}_${timestamp}.xls`;
    },
    async confirmExportExcel() {
      const selectedKeys = Array.isArray(this.exportSelectedFields)
        ? this.exportSelectedFields.slice()
        : [];
      const columns = this.exportFieldOptions.filter((item) => selectedKeys.includes(item.key));
      if (!columns.length) {
        this.$message.warning("至少选择一个导出字段");
        return;
      }

      this.exporting = true;
      try {
        const rows = await this.fetchAllLedgerRowsForExport();
        if (!rows.length) {
          this.$message.warning("当前筛选条件下暂无可导出数据");
          return;
        }
        const html = this.buildExportTableHtml(columns, rows);
        this.downloadExcelFile(html, this.getExportFileName());
        this.exportDialogVisible = false;
        this.$message.success(`导出完成，共 ${rows.length} 条`);
      } catch (e) {
        this.$message.error("导出失败，请稍后重试");
      } finally {
        this.exporting = false;
      }
    },
    isCustomerAiEnabled(row) {
      const value = row && row.enable_ai_response !== undefined
        ? row.enable_ai_response
        : (row && row.Ecustomers);
      return Number(value) === 1;
    },
    getDealStatusLabel(row) {
      return this.parseIsDealFlag(row && row.is_deal) ? "是" : "否";
    },
    async toggleCustomerAi(row) {
      if (!this.canManageAiSwitch) {
        this.$message.warning("仅 admin、吴强、刘方韬、郭晓红 可操作AI开关");
        return;
      }
      if (!row || !row.id) return;
      if (this.aiSwitchLoadingMap[row.id]) return;

      const nextValue = this.isCustomerAiEnabled(row) ? 0 : 1;
      this.$set(this.aiSwitchLoadingMap, row.id, true);
      try {
        await customerApi.updateCustomerAiSwitch(row.id, { Ecustomers: nextValue });
        this.$set(row, "enable_ai_response", nextValue);
        this.$set(row, "Ecustomers", nextValue);
        this.$message.success(nextValue === 1 ? "AI已开启" : "AI已关闭");
      } catch (e) {
        this.$message.error("更新AI开关失败");
      } finally {
        this.$set(this.aiSwitchLoadingMap, row.id, false);
      }
    },
    collectLedgerCustomerIds() {
      if (!Array.isArray(this.ledger) || !this.ledger.length) return [];
      return Array.from(
        new Set(
          this.ledger
            .map((row) => Number(row && row.id ? row.id : 0))
            .filter((id) => id > 0)
        )
      );
    },
    applyAiSwitchStatuses(items) {
      if (!Array.isArray(items) || !items.length) return;

      const statusById = new Map();
      items.forEach((item) => {
        const id = Number(item && item.id ? item.id : 0);
        if (id <= 0) return;
        const enabled = Number(item && item.enable_ai_response) === 1 ? 1 : 0;
        statusById.set(id, enabled);
      });

      if (!statusById.size || !Array.isArray(this.ledger)) return;

      this.ledger.forEach((row) => {
        const id = Number(row && row.id ? row.id : 0);
        if (id <= 0 || !statusById.has(id)) return;
        const enabled = statusById.get(id);
        this.$set(row, "enable_ai_response", enabled);
        this.$set(row, "Ecustomers", enabled);
      });
    },
    async refreshAiSwitchStatuses(options = {}) {
      const silent = !!(options && options.silent);
      if (this.isLeadMode) return;

      const ids = this.collectLedgerCustomerIds();
      if (!ids.length) return;

      if (this.aiSwitchRefreshLoading) {
        this.aiSwitchRefreshPending = true;
        return;
      }

      this.aiSwitchRefreshLoading = true;
      try {
        const data = await customerApi.getCustomerAiSwitchStatuses({ ids });
        this.applyAiSwitchStatuses(data && data.items);
      } catch (e) {
        if (!silent) {
          this.$message.error("刷新客户AI状态失败");
        }
      } finally {
        this.aiSwitchRefreshLoading = false;
        if (this.aiSwitchRefreshPending) {
          this.aiSwitchRefreshPending = false;
          this.refreshAiSwitchStatuses({ silent: true });
        }
      }
    },
    startAiSwitchStatusAutoRefresh() {
      this.stopAiSwitchStatusAutoRefresh();
      if (this.isLeadMode) return;

      this.aiSwitchRefreshTimerId = setInterval(() => {
        if (this.loading || this.syncingCustomers || this.chatVisible || this.summaryVisible || this.exportDialogVisible) return;
        this.refreshAiSwitchStatuses({ silent: true });
      }, this.aiSwitchRefreshInterval);
    },
    stopAiSwitchStatusAutoRefresh() {
      if (this.aiSwitchRefreshTimerId) {
        clearInterval(this.aiSwitchRefreshTimerId);
        this.aiSwitchRefreshTimerId = null;
      }
      this.aiSwitchRefreshPending = false;
    },
    async refreshCustomerTable(options = {}) {
      const silent = !!options.silent;
      const resetPage = options.resetPage !== false;

      if (this.syncingCustomers || this.loading) return;
      this.syncingCustomers = true;
      try {
        const data = await customerApi.refreshCustomerLedger();
        if (!silent) {
          this.$message.success(data && data.message ? data.message : "客户数据刷新完成");
        }
        if (resetPage) {
          this.pagination.current_page = 1;
        }
        await this.loadLedger({ force: true });
      } catch (e) {
        if (!silent) {
          this.$message.error(e && e.message ? e.message : "刷新客户数据失败");
        }
      } finally {
        this.syncingCustomers = false;
      }
    },
    startLedgerAutoRefresh() {
      this.stopLedgerAutoRefresh();
      this.ledgerRefreshTimerId = setInterval(() => {
        if (this.loading || this.syncingCustomers || this.chatVisible || this.summaryVisible || this.exportDialogVisible) return;
        this.loadLedger();
      }, this.ledgerRefreshInterval);
    },
    stopLedgerAutoRefresh() {
      if (this.ledgerRefreshTimerId) {
        clearInterval(this.ledgerRefreshTimerId);
        this.ledgerRefreshTimerId = null;
      }
    },
    async viewConversation(row) {
      this.stopConversationAutoRefresh();
      this.activeRow = row || {};
      this.chatVisible = true;
      this.conversationTab = "all";
      this.conversation = [];
      this.conversationImageLoadFailedMap = {};
      this.resetChatComposer();
      await this.fetchConversationRecords(true);
      this.startConversationAutoRefresh();
    },
    resetChatComposer() {
      this.chatSending = false;
      this.chatComposerText = "";
      this.chatComposerImage = null;
      this.chatComposerImageName = "";
      this.clearConversationImageUploadFiles();
    },
    getConversationRecordKey(item) {
      const chatLogId = Number(item && item.chat_log_id ? item.chat_log_id : 0);
      if (chatLogId > 0) return `log:${chatLogId}`;

      const msgId = String((item && item.msg_id) || "").trim();
      if (msgId !== "") return `msg:${msgId}`;

      const time = String((item && item.time) || "").trim();
      const sender = String((item && item.sender_name) || "").trim();
      const speaker = String((item && item.speaker) || "").trim();
      const content = String((item && item.content) || "").trim();
      return `fallback:${time}|${sender}|${speaker}|${content}`;
    },
    mergeConversationIncremental(existing, incomingAsc) {
      const base = Array.isArray(existing) ? existing.slice() : [];
      const incoming = Array.isArray(incomingAsc) ? incomingAsc : [];
      const indexByKey = new Map();
      base.forEach((item, index) => {
        indexByKey.set(this.getConversationRecordKey(item), index);
      });
      for (const item of incoming) {
        const key = this.getConversationRecordKey(item);
        if (indexByKey.has(key)) {
          const targetIndex = indexByKey.get(key);
          const current = base[targetIndex] || {};
          base.splice(targetIndex, 1, { ...current, ...item });
          continue;
        }
        indexByKey.set(key, base.length);
        base.push(item);
      }
      return base;
    },
    extractConversationPageRecords(data) {
      return Array.isArray(data && data.records)
        ? data.records
        : (Array.isArray(data && data.items) ? data.items : []);
    },
    async fetchConversationRecords(showLoading = false) {
      if (!this.activeRow || !this.activeRow.id) return;

      const requestToken = Date.now();
      this.conversationLoadToken = requestToken;
      if (showLoading) {
        this.chatLoading = true;
      } else {
        if (this.chatRefreshing) return;
        this.chatRefreshing = true;
      }

      try {
        const perPage = 100;
        const order = "desc";

        // Incremental refresh: only pull latest page and merge.
        if (!showLoading) {
          const latestData = await customerApi.getCustomerConversation(this.activeRow.id, {
            page: 1,
            per_page: perPage,
            order
          });
          if (this.conversationLoadToken !== requestToken) return;

          const latestDesc = this.extractConversationPageRecords(latestData);
          const latestAsc = latestDesc.slice().reverse();
          this.conversation = this.mergeConversationIncremental(this.conversation, latestAsc);
          return;
        }

        const firstData = await customerApi.getCustomerConversation(this.activeRow.id, {
          page: 1,
          per_page: perPage,
          order
        });
        if (this.conversationLoadToken !== requestToken) return;

        const firstPageDesc = this.extractConversationPageRecords(firstData);
        const firstPagination = firstData && firstData.pagination ? firstData.pagination : {};
        const parsedLastPage = Number(firstPagination.last_page);
        const lastPage = Number.isFinite(parsedLastPage) && parsedLastPage > 0
          ? Math.floor(parsedLastPage)
          : 1;

        const recordsByPage = { 1: firstPageDesc };
        if (lastPage > 1) {
          const pendingPages = [];
          for (let page = 2; page <= lastPage; page += 1) {
            pendingPages.push(page);
          }

          const concurrency = 4;
          for (let index = 0; index < pendingPages.length; index += concurrency) {
            const batchPages = pendingPages.slice(index, index + concurrency);
            const batchResults = await Promise.all(
              batchPages.map((page) => customerApi.getCustomerConversation(this.activeRow.id, {
                page,
                per_page: perPage,
                order
              }))
            );
            if (this.conversationLoadToken !== requestToken) return;

            batchPages.forEach((page, offset) => {
              recordsByPage[page] = this.extractConversationPageRecords(batchResults[offset]);
            });
          }
        }

        const allAsc = [];
        for (let page = lastPage; page >= 1; page -= 1) {
          const pageDesc = Array.isArray(recordsByPage[page]) ? recordsByPage[page] : [];
          for (let i = pageDesc.length - 1; i >= 0; i -= 1) {
            allAsc.push(pageDesc[i]);
          }
        }
        this.conversation = allAsc;
        this.conversationImageLoadFailedMap = {};
        return;
      } catch (e) {
        if (showLoading) {
          this.conversation = [];
          this.$message.error("加载对话记录失败");
        }
      } finally {
        if (showLoading) {
          this.chatLoading = false;
        } else {
          this.chatRefreshing = false;
        }
      }
    },
    async refreshConversation() {
      await this.fetchConversationRecords(false);
    },
    handleConversationImageChange(file) {
      const rawFile = (file && file.raw) ? file.raw : file;
      if (!rawFile) return;
      const isImage = String(rawFile.type || "").toLowerCase().startsWith("image/");
      if (!isImage) {
        this.$message.warning("仅支持上传图片文件");
        this.clearConversationImage();
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (Number(rawFile.size || 0) > maxSize) {
        this.$message.warning("图片大小不能超过10MB");
        this.clearConversationImage();
        return;
      }

      this.chatComposerImage = rawFile;
      this.chatComposerImageName = String(rawFile.name || "已选择图片");
    },
    handleConversationImageExceed(files) {
      const latest = Array.isArray(files) && files.length ? files[files.length - 1] : null;
      if (!latest) return;
      this.clearConversationImageUploadFiles();
      this.handleConversationImageChange({ raw: latest });
    },
    clearConversationImage() {
      this.chatComposerImage = null;
      this.chatComposerImageName = "";
      this.clearConversationImageUploadFiles();
    },
    clearConversationImageUploadFiles() {
      const uploadRef = this.$refs.chatImageUpload;
      if (uploadRef && typeof uploadRef.clearFiles === "function") {
        uploadRef.clearFiles();
      }
    },
    async sendConversationMessage() {
      if (!this.activeRow || !this.activeRow.id) return;
      if (this.chatSending) return;

      const content = String(this.chatComposerText || "").trim();
      const image = this.chatComposerImage || null;
      if (!content && !image) {
        this.$message.warning("请输入文本或上传图片后再发送");
        return;
      }

      this.chatSending = true;
      try {
        const formData = new FormData();
        if (content) {
          formData.append("content", content);
        }
        if (image) {
          formData.append("image", image, image.name || "image.jpg");
        }

        const data = await customerApi.sendCustomerConversationMessage(this.activeRow.id, formData);
        this.$message.success((data && data.message) || "发送成功");
        this.$set(this.activeRow, "enable_ai_response", 0);
        this.$set(this.activeRow, "Ecustomers", 0);
        const targetRow = Array.isArray(this.ledger)
          ? this.ledger.find((row) => Number(row && row.id) === Number(this.activeRow.id))
          : null;
        if (targetRow) {
          this.$set(targetRow, "enable_ai_response", 0);
          this.$set(targetRow, "Ecustomers", 0);
        }
        this.resetChatComposer();
        await this.fetchConversationRecords(false);
      } catch (e) {
        const message = String((e && e.message) || "").trim();
        if (!message) {
          this.$message.error("发送失败，请稍后重试");
        }
      } finally {
        this.chatSending = false;
      }
    },
    startConversationAutoRefresh() {
      this.stopConversationAutoRefresh();
      this.chatRefreshTimerId = setInterval(() => {
        if (!this.chatVisible) return;
        this.fetchConversationRecords(false);
      }, this.chatRefreshInterval);
    },
    stopConversationAutoRefresh() {
      if (this.chatRefreshTimerId) {
        clearInterval(this.chatRefreshTimerId);
        this.chatRefreshTimerId = null;
      }
    },
    async viewSummary(row) {
      if (this.isLeadMode) return;
      this.summaryRow = row || {};
      this.summaryVisible = true;
      this.summaryLoading = true;
      this.summaryResult = {
        brief: "",
        key_info: [],
        highlights: [],
        deal_advice: [],
        source: "",
        stats: {}
      };

      try {
        const data = await customerApi.getCustomerSummary(row.id);
        const summary = data && data.summary ? data.summary : {};
        this.summaryResult = {
          brief: summary.brief || "",
          key_info: Array.isArray(summary.key_info) ? summary.key_info : [],
          highlights: Array.isArray(summary.highlights) ? summary.highlights : [],
          deal_advice: Array.isArray(summary.deal_advice) ? summary.deal_advice : [],
          source: summary.source || "",
          stats: summary.stats || {}
        };
      } catch (e) {
        this.summaryResult = {
          brief: "",
          key_info: [],
          highlights: [],
          deal_advice: [],
          source: "",
          stats: {}
        };
        this.$message.error("加载聊天总结失败");
      } finally {
        this.summaryLoading = false;
      }
    },
    closeChat() {
      this.chatVisible = false;
      this.conversationTab = "all";
      this.conversationLoadToken = Date.now();
      this.chatLoading = false;
      this.chatRefreshing = false;
      this.resetChatComposer();
      this.stopConversationAutoRefresh();
    },
    resolveSpeakerClass(item) {
      if (String(item && item.msg_type) === "auto_reply") return "ai";
      return item && item.speaker === "sales" ? "sales" : "client";
    },
    resolveSpeakerLabel(item) {
      if (String(item && item.msg_type) === "auto_reply") return "AI";
      return item && item.speaker === "sales" ? "销售" : "客户";
    },
    isConversationAutoReply(item) {
      return String(item && item.msg_type) === "auto_reply";
    },
    shouldShowConversationAiPushStatus(item) {
      return this.isConversationAutoReply(item);
    },
    getConversationAiPushStatusText(item) {
      if (!this.isConversationAutoReply(item)) return "";
      const status = String((item && item.ai_push_status) || "").toLowerCase();
      const sent = Number(item && item.ai_push_sent) === 1;
      if (sent || status === "success" || status === "ok" || status === "sent") return "已发微信";
      if (status === "failed" || status === "error") return "发送失败";
      return "未发微信";
    },
    getConversationAiPushStatusClass(item) {
      if (!this.isConversationAutoReply(item)) return "";
      const status = String((item && item.ai_push_status) || "").toLowerCase();
      const sent = Number(item && item.ai_push_sent) === 1;
      if (sent || status === "success" || status === "ok" || status === "sent") return "is-success";
      if (status === "failed" || status === "error") return "is-failed";
      return "is-pending";
    },
    parseConversationJson(content, maxDepth = 2) {
      let current = content;
      for (let i = 0; i <= maxDepth; i += 1) {
        if (current == null) return null;
        if (typeof current === "object") return current;
        if (typeof current !== "string") return null;

        const trimmed = current.trim();
        if (!trimmed) return null;
        if (!["{", "[", "\""].includes(trimmed[0])) return null;

        try {
          current = JSON.parse(trimmed);
        } catch (e) {
          return null;
        }
      }
      return typeof current === "object" ? current : null;
    },
    parseConversationPayload(item) {
      const payload = this.parseConversationJson(item && item.content);
      if (!payload || Array.isArray(payload)) return payload;
      if (payload.msgData && typeof payload.msgData === "object") return payload.msgData;
      return payload;
    },
    parseConversationOriginPayload(item) {
      const payload = this.parseConversationJson(item && item.origin_data);
      if (!payload || Array.isArray(payload)) return null;
      if (payload.msgData && typeof payload.msgData === "object") return payload.msgData;
      return payload;
    },
    normalizeConversationMediaUrl(url) {
      if (!url) return "";
      let value = String(url).trim();
      if (!value) return "";
      if (
        (value.startsWith("\"") && value.endsWith("\""))
        || (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1).trim();
      }
      value = value.replace(/\\\//g, "/").replace(/&amp;/gi, "&");
      if (value.startsWith("//")) {
        value = `https:${value}`;
      }
      return /^https?:\/\//i.test(value) ? value : "";
    },
    getConversationRawUrl(item) {
      const content = item && item.content;
      if (typeof content !== "string") return "";
      const trimmed = content.trim();
      const normalized = this.normalizeConversationMediaUrl(trimmed);
      if (normalized) return normalized;
      const matched = trimmed.match(/https?:\/\/[^\s"'<>]+/i);
      return matched ? this.normalizeConversationMediaUrl(matched[0]) : "";
    },
    getConversationMediaUrlFromPayload(payload) {
      if (!payload || Array.isArray(payload)) return "";
      return this.normalizeConversationMediaUrl(
        payload.fileBigHttpUrl
          || payload.fileMiddleHttpUrl
          || payload.fileThumbHttpUrl
          || payload.fileHttpUrl
          || payload.previewUrl
          || payload.image_display_url
          || payload.localImageUrl
          || payload.local_image_url
          || payload.cloudUrl
          || payload.fileUrl
          || payload.voiceHttpUrl
          || payload.videoHttpUrl
          || payload.downloadUrl
          || payload.url
          || ""
      );
    },
    getConversationMediaUrl(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      return this.getConversationMediaUrlFromPayload(payload)
        || this.getConversationMediaUrlFromPayload(originPayload);
    },
    getConversationCardPayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || Array.isArray(candidate) || typeof candidate !== "object") continue;
        if (
          candidate.appId
          || candidate.appName
          || candidate.title
          || candidate.pagePath
          || candidate.username
          || candidate.iconUrl
          || candidate.url
          || candidate.appMediaUrl
          || candidate.desc
        ) {
          return candidate;
        }
      }
      return null;
    },
    isConversationAppCard(item) {
      const type = String(item && item.msg_type);
      if (type === "13" || type === "78") return true;
      return !!this.getConversationCardPayload(item);
    },
    getConversationAppTitle(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.title || payload.name || "";
    },
    getConversationAppDesc(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.desc || payload.content || "";
    },
    getConversationAppName(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.appName || payload.username || "";
    },
    getConversationAppIconUrl(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return this.normalizeConversationMediaUrl(
        payload.iconUrl
          || payload.coverImageUrl
          || payload.coverImageHttpUrl
          || payload.coverImageThumbUrl
          || ""
      );
    },
    getConversationAppUrl(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return this.normalizeConversationMediaUrl(
        payload.url
          || payload.appMediaUrl
          || payload.linkUrl
          || ""
      );
    },
    getConversationAppPath(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      if (payload.pagePath) return `小程序路径：${payload.pagePath}`;
      if (payload.username) return `小程序账号：${payload.username}`;
      return "";
    },
    getConversationBase64RawPayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || Array.isArray(candidate) || typeof candidate !== "object") continue;
        if (candidate.base64RawData) return candidate;
      }
      return null;
    },
    isConversationBase64RawMessage(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload) return false;
      return !this.isConversationCallRecord(item) && !this.isConversationRevoke(item);
    },
    getConversationBase64RawText(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload) return "系统消息";
      if (payload.recordWording) return String(payload.recordWording);
      return "系统加密消息";
    },
    getConversationBase64RawLength(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload || !payload.base64RawData) return "";
      return String(payload.base64RawData).length;
    },
    isLikelyJsonString(value) {
      if (typeof value !== "string") return false;
      const trimmed = value.trim();
      if (!trimmed) return false;
      return trimmed.startsWith("{") || trimmed.startsWith("[");
    },
    getConversationDetailTextFromPayload(payload) {
      if (!payload || typeof payload !== "object") return "";

      const content = typeof payload.content === "string" ? payload.content.trim() : "";
      if (content && !this.isLikelyJsonString(content)) return content;

      const detailText = Array.isArray(payload.moreDetail)
        ? payload.moreDetail
            .map((detail) => {
              if (!detail || typeof detail !== "object") return "";
              return typeof detail.text === "string" ? detail.text.trim() : "";
            })
            .filter(Boolean)
            .join("")
            .trim()
        : "";
      if (detailText) return detailText;

      if (payload.reply && typeof payload.reply === "object") {
        const replyContent = typeof payload.reply.content === "string" ? payload.reply.content.trim() : "";
        if (replyContent && !this.isLikelyJsonString(replyContent)) return replyContent;

        const replyDetailText = Array.isArray(payload.reply.moreDetail)
          ? payload.reply.moreDetail
              .map((detail) => {
                if (!detail || typeof detail !== "object") return "";
                return typeof detail.text === "string" ? detail.text.trim() : "";
              })
              .filter(Boolean)
              .join("")
              .trim()
          : "";
        if (replyDetailText) return replyDetailText;
      }

      return "";
    },
    getConversationPlainText(item) {
      const payload = this.parseConversationPayload(item);
      if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        if (payload.base64RawData) return this.getConversationBase64RawText(item);
        const payloadText = this.getConversationDetailTextFromPayload(payload);
        if (payloadText) return payloadText;
      }
      const content = item && item.content;
      if (content == null) return "";
      if (typeof content === "string") {
        if (this.isLikelyJsonString(content)) return "";
        return content;
      }
      try {
        return JSON.stringify(content);
      } catch (e) {
        return String(content);
      }
    },
    getSummaryHighlightContent(item) {
      if (!item || typeof item !== "object") return "";
      const content = typeof item.content === "string" ? item.content : "";
      if (!content) return "";

      const payload = this.parseConversationJson(content);
      if (!payload || Array.isArray(payload) || typeof payload !== "object") {
        return content;
      }

      const payloadText = this.getConversationDetailTextFromPayload(payload);
      return payloadText || (this.isLikelyJsonString(content) ? "" : content);
    },
    isConversationImage(item) {
      const type = String(item && item.msg_type);
      return ["7", "14", "29", "101", "104"].includes(type);
    },
    getConversationRowKey(item) {
      return (item && (item.id || item.msg_id || item.time || item.sender_name)) || "";
    },
    hasConversationImageLoadFailed(item) {
      const key = this.getConversationRowKey(item);
      return key ? !!this.conversationImageLoadFailedMap[key] : false;
    },
    handleConversationImageError(item) {
      const key = this.getConversationRowKey(item);
      if (!key) return;
      this.$set(this.conversationImageLoadFailedMap, key, true);
    },
    canPreviewConversationImage(item) {
      return !!this.getConversationImageUrl(item) && !this.hasConversationImageLoadFailed(item);
    },
    getConversationImageUnavailableText(item) {
      if (this.hasConversationImageLoadFailed(item)) {
        return "图片链接已失效或已过期";
      }
      return "图片消息（暂无可预览链接）";
    },
    getConversationImageUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    isConversationVoice(item) {
      return String(item && item.msg_type) === "16";
    },
    getConversationVoiceUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    getConversationVoiceDuration(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      const seconds = Number(payload.voiceTime || payload.duration || 0);
      return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : "";
    },
    isConversationFile(item) {
      const type = String(item && item.msg_type);
      return type === "15" || type === "17";
    },
    getConversationFileUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    getConversationFileName(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      return payload.filename || payload.fileName || payload.name || "";
    },
    isConversationVideo(item) {
      const type = String(item && item.msg_type);
      return type === "20" || type === "102";
    },
    getConversationVideoUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    isConversationCallRecord(item) {
      const type = String(item && item.msg_type);
      const payload = this.parseConversationPayload(item);
      return type === "40" || (
        !!payload
        && (payload.recordWording !== undefined || payload.recordType !== undefined || payload.inviteType !== undefined)
      );
    },
    getConversationCallRecordText(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "通话记录";
      if (payload.recordWording) return String(payload.recordWording);
      const recordType = Number(payload.recordType);
      if (recordType === 2) return "对方已拒绝";
      if (recordType === 3) return "对方未接听";
      if (recordType === 4) return "已取消";
      if (recordType === 5) return "通话已结束";
      return "通话记录";
    },
    getConversationCallDuration(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      const seconds = Number(payload.duration || 0);
      if (!Number.isFinite(seconds) || seconds <= 0) return "";
      const mm = Math.floor(seconds / 60);
      const ss = seconds % 60;
      return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    },
    isConversationReadReceipt(item) {
      const type = String(item && item.msg_type);
      return type === "2001" || type === "2005";
    },
    getConversationReadReceiptText(item) {
      const type = String(item && item.msg_type);
      if (type === "2001") return "消息已读通知";
      if (type === "2005") return "消息未读通知";
      return "消息状态通知";
    },
    getConversationRevokePayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
        if (
          candidate.revokeMsgUniqueIdentifier !== undefined
          || candidate.revokeUserId !== undefined
          || candidate.revokeTime !== undefined
        ) {
          return candidate;
        }
      }
      return null;
    },
    isConversationRevoke(item) {
      const type = String(item && item.msg_type);
      if (type === "10000" || type === "10002") return true;
      return !!this.getConversationRevokePayload(item);
    },
    formatConversationUnixTimestamp(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return "";
      const ms = numeric > 1e12 ? numeric : numeric * 1000;
      const date = new Date(ms);
      if (Number.isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const second = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    getConversationRevokeInfo(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      const userId = String(payload.revokeUserId ?? "").trim();
      const time = this.formatConversationUnixTimestamp(payload.revokeTime);
      const parts = [];
      if (userId) parts.push(`撤回人ID：${userId}`);
      if (time) parts.push(`撤回时间：${time}`);
      return parts.join(" · ");
    },
    getConversationRevokeMsgId(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      return String(payload.revokeMsgUniqueIdentifier ?? "").trim();
    },
    getConversationRevokeOriginalTypeLabel(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      const originalType = payload?.extras?.signedMsg?.originalContentType;
      if (originalType == null) return "";
      return this.getConversationMessageTypeLabelByCode(originalType);
    },
    getConversationMessageTypeLabelByCode(msgType) {
      const type = String(msgType);
      if (type === "auto_reply") return "AI回复";
      if (type === "0") return "文本消息";
      if (type === "2") return "文本消息";
      if (type === "13") return "链接消息";
      if (["7", "14", "29", "101", "104"].includes(type)) return "图片消息";
      if (type === "15" || type === "17") return "文件消息";
      if (type === "16") return "语音消息";
      if (type === "18") return "XML消息";
      if (type === "20" || type === "102") return "视频消息";
      if (type === "78") return "小程序消息";
      if (type === "40") return "通话记录";
      if (type === "10000" || type === "10002") return "撤回消息";
      if (type === "2001") return "已读通知";
      if (type === "2005") return "未读通知";
      if (type === "105") return "Markdown消息";
      return "用户消息";
    },
    getDealProbabilityScore(row) {
      const score = Number(row && row.deal_probability_score);
      if (!Number.isFinite(score)) return 0;
      return Math.max(0, Math.min(100, Math.round(score)));
    },
    getDealProbabilityLabel(row) {
      const label = row && row.deal_probability_label;
      if (typeof label === "string" && label.trim()) return label.trim();
      return "待评估";
    },
    getDealProbabilityTagType(row) {
      const tag = String((row && row.deal_probability_tag_type) || "").trim();
      if (["success", "warning", "info", "danger", ""].includes(tag)) return tag;
      return "info";
    },
    getDealProbabilityReasonText(row) {
      const reasons = Array.isArray(row && row.deal_probability_reasons)
        ? row.deal_probability_reasons.filter((item) => typeof item === "string" && item.trim())
        : [];
      if (!reasons.length) return "暂无分析说明";
      return reasons.join("；");
    },
    getPaymentCodeSentLabel(row) {
      const value = row && row.payment_code_sent;
      return Number(value) === 1 || value === true ? "已发送" : "未发送";
    },
    getDealStatusLabel(row) {
      const value = row && row.is_deal;
      return Number(value) === 1 || value === true ? "是" : "否";
    },
    resolveCustomerTags(row) {
      const orderedTags = [
        "价格贵",
        "不信任",
        "决策人",
        "孩子",
        "自己过错",
        "内心犹豫",
        "没钱",
        "咨询类",
        "做对比",
        "证据",
        "对话不足",
        "还住一起"
      ];
      const allowSet = new Set(orderedTags);
      const raw = row && row.customer_tags;
      let parsed = [];
      if (Array.isArray(raw)) {
        parsed = raw
          .map((item) => String(item || "").trim())
          .filter((item, idx, arr) => item && arr.indexOf(item) === idx);
      }
      if (typeof raw === "string" && raw.trim()) {
        parsed = raw
          .split(/[，,、]/)
          .map((item) => item.trim())
          .filter((item, idx, arr) => item && arr.indexOf(item) === idx);
      }
      if (!parsed.length) return [];
      const parsedSet = new Set(parsed.filter((tag) => allowSet.has(tag)));
      return orderedTags.filter((tag) => parsedSet.has(tag));
    },
    getCustomerTagType(tag) {
      const value = String(tag || "").trim();
      const typeMap = {
        "价格贵": "danger",
        "不信任": "warning",
        "决策人": "info",
        "孩子": "success",
        "自己过错": "warning",
        "内心犹豫": "info",
        "没钱": "danger",
        "咨询类": "info",
        "做对比": "danger",
        "证据": "warning",
        "对话不足": "warning",
        "还住一起": "info"
      };
      return typeMap[value] || "info";
    },
    closeSummary() {
      this.summaryVisible = false;
    }
  },
  computed: {
    canManageAiSwitch() {
      const username = String(localStorage.getItem("username") || "").trim();
      if (localStorage.getItem("is_admin") === "1") return true;
      if (["admin@mianzhi.com", "admin"].includes(username.toLowerCase())) return true;
      const allowNames = new Set(["吴强", "刘方韬", "郭晓红"]);
      if (allowNames.has(username)) return true;
      try {
        const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
        if (Number(user?.is_admin) === 1 || user?.is_admin === true) return true;
        return allowNames.has(String(user?.name || "").trim());
      } catch (e) {
        return false;
      }
    },
    isLeadMode() {
      return this.leadMode === true;
    },
    pageTitle() {
      return this.isLeadMode ? "线索列表台账" : "客户列表台账";
    },
    exportFieldOptions() {
      const fields = [
        { key: "index", label: "序号" },
        { key: "customer_wechat_name", label: "客户微信名" },
        { key: "sales_name", label: "对接销售" },
        { key: "customer_tags", label: "客户标签" },
        { key: "first_chat_time", label: "首次聊天时间" },
        { key: "ai_last_chat_time", label: "AI最后对话时间" },
      ];
      if (!this.isLeadMode) {
        fields.splice(3, 0, { key: "is_deal", label: "成单" });
        fields.splice(5, 0, { key: "ai_intervention_count", label: "AI次数" });
      }
      return fields;
    },
    refreshButtonText() {
      return this.isLeadMode ? "刷新线索数据" : "刷新客户数据";
    },
    isCompactDesktop() {
      return !this.isMobileViewport && this.viewportWidth <= 1600;
    },
    operationColumnWidth() {
      if (this.isLeadMode) return 112;
      return this.isCompactDesktop ? 198 : 214;
    },
    filteredConversation() {
      if (this.conversationTab === "ai") {
        return this.conversation.filter((item) => {
          if (!item) return false;
          return item.speaker === "client" || String(item.msg_type) === "auto_reply";
        });
      }
      if (this.conversationTab === "sales") {
        return this.conversation.filter((item) => {
          if (!item) return false;
          if (item.speaker === "client") return true;
          return item.speaker === "sales" && String(item.msg_type) !== "auto_reply";
        });
      }
      return this.conversation;
    },
    summaryCoreInfo() {
      const list = Array.isArray(this.summaryResult && this.summaryResult.key_info)
        ? this.summaryResult.key_info
        : [];
      const allowLabels = new Set(["当事人信息", "当事人情况", "当事人诉求"]);
      return list.filter((item) => item && allowLabels.has(String(item.label || "").trim()));
    },
    summarySourceLabel() {
      return this.summaryResult && this.summaryResult.source === "ai" ? "AI总结" : "规则总结";
    }
  }
};
</script>

<style scoped>
.filter-label {
  font-size: 12px;
  color: #334155;
  white-space: nowrap;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.page-header-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.page-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.filter-query-btn {
  margin-left: 0;
}

.ledger-refresh-text {
  font-size: 12px;
  color: #64748b;
}

.mobile-filter-toggle {
  border-color: #cbd5e1;
  color: #334155;
}

.export-btn {
  border-color: #cbd5e1;
  color: #334155;
}

.name-filter {
  width: 180px;
}

.sales-filter {
  width: 110px;
}

.ai-filter {
  width: 160px;
}

.deal-filter {
  width: 88px;
}

.action-gap {
  margin-left: 8px;
}

.mobile-ledger-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 10px 12px;
  display: grid;
  gap: 8px;
  align-content: start;
}

.mobile-ledger-head {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 8px;
}

.mobile-ledger-head span {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.mobile-ledger-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  align-items: stretch;
}

.mobile-ledger-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
}

.mobile-wechat-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mobile-wechat-name {
  color: #0f172a;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

.mobile-wechat-id {
  color: #64748b;
  font-size: 12px;
  line-height: 1.25;
  word-break: break-all;
}

.mobile-sales-text {
  color: #334155;
  font-size: 13px;
  line-height: 1.35;
  word-break: break-all;
}

.mobile-ledger-field--sales {
  align-items: center;
}

.mobile-ledger-field--tags {
  align-items: center;
}

.mobile-ledger-field--actions {
  align-items: stretch;
}

.mobile-ledger-tag-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.mobile-ledger-tag-empty {
  color: #94a3b8;
  font-size: 12px;
}

.mobile-ledger-metrics {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding-top: 2px;
}

.mobile-ledger-metric {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mobile-ledger-metric-label {
  color: #64748b;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
}

.mobile-ledger-metric-value {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.operation-cell {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.operation-cell .btn {
  padding: 2px 8px;
  min-width: 50px;
  font-size: 12px;
  line-height: 1.35;
}

.operation-cell .action-gap {
  margin-left: 0;
}

.operation-cell.mobile {
  display: grid;
  width: 100%;
  gap: 6px;
}

.operation-cell.mobile .btn {
  margin-left: 0;
  width: 100%;
  padding: 4px 6px;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
}

.table-shell {
  flex: 1;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.table-shell .el-table {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.table-shell .el-table__body-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.overflow-x-auto {
  flex: 1;
  min-height: 0;
}

.chat-modal {
  position: fixed;
  inset: 0;
  background: rgba(2, 8, 23, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 22;
  backdrop-filter: blur(4px);
  box-sizing: border-box;
  overflow-y: auto;
}

.chat-card {
  width: min(760px, 100%);
  max-height: min(88vh, 780px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e7efeb;
  border-radius: 16px;
  box-shadow: 0 22px 46px rgba(15, 23, 42, 0.28);
  padding: 12px;
}

.chat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.chat-head h3 {
  margin: 0;
}

.chat-head-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.chat-refresh-text {
  font-size: 12px;
  color: #64748b;
}

.chat-refresh-btn {
  cursor: pointer;
}

.chat-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: grid;
  gap: 8px;
  padding-right: 4px;
  overscroll-behavior: contain;
}

.chat-composer {
  border-top: 1px solid #e2e8f0;
  margin-top: 10px;
  padding-top: 10px;
  display: grid;
  gap: 8px;
}

.chat-composer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.chat-image-upload {
  display: inline-flex;
}

.chat-image-name {
  color: #334155;
  font-size: 12px;
  max-width: 320px;
  word-break: break-all;
}

.chat-image-remove {
  padding: 0;
}

.conversation-tabs {
  margin-bottom: 8px;
}

.summary-card {
  width: min(860px, 100%);
}

.summary-body {
  gap: 10px;
}

.summary-block {
  border: 1px solid #e7efeb;
  border-radius: 10px;
  padding: 10px;
  background: #fcfffd;
}

.summary-block h4 {
  margin: 0 0 8px;
  color: #0f172a;
}

.summary-source {
  margin-left: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
}

.summary-block p {
  margin: 0;
  color: #334155;
  line-height: 1.6;
}

.summary-kv {
  display: grid;
  gap: 8px;
}

.summary-kv-row {
  display: grid;
  grid-template-columns: 86px 1fr;
  gap: 8px;
}

.summary-k {
  color: #0f766e;
  font-weight: 600;
}

.summary-v {
  color: #334155;
  line-height: 1.5;
}

.summary-highlight {
  border-left: 3px solid #14b8a6;
  padding: 6px 0 6px 10px;
  margin-bottom: 8px;
  background: #f7fcfb;
}

.summary-highlight small {
  color: #64748b;
}

.summary-highlight p {
  margin-top: 4px;
}

.summary-advice {
  display: grid;
  gap: 8px;
}

.summary-advice-item {
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 8px;
  align-items: start;
}

.summary-advice-index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #0f766e;
  color: #fff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.summary-advice-item p {
  margin: 0;
  color: #334155;
  line-height: 1.6;
}

.conv-empty {
  border: 1px dashed #cfe1da;
  border-radius: 10px;
  padding: 16px;
  color: #64748b;
  text-align: center;
  background: #f8fcfa;
}

.chat-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.chat-item.sales {
  justify-content: flex-end;
}

.chat-item.client {
  justify-content: flex-start;
}

.chat-bubble {
  max-width: min(560px, 80%);
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid #deebe6;
  background: #f8fcfa;
  color: #0f172a;
  line-height: 1.5;
  font-size: 13px;
}

.chat-item.sales .chat-bubble {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.chat-item.ai {
  justify-content: flex-end;
}

.chat-item.ai .chat-bubble {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.conversation-image-link,
.conversation-file-link {
  cursor: pointer;
}

.conversation-image {
  max-width: 220px;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.conversation-audio {
  width: 260px;
  max-width: 100%;
}

.conversation-video {
  width: 280px;
  max-width: 100%;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #000;
}

.conversation-file-link {
  color: #0369a1;
  text-decoration: underline;
}

.conversation-muted {
  color: #6b7280;
}

.conversation-call-record {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-call-record-title {
  color: #1f2937;
  font-weight: 600;
}

.conversation-call-record-sub {
  color: #6b7280;
  font-size: 12px;
}

.conversation-app-card {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  max-width: 360px;
}

.conversation-app-header {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.conversation-app-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.conversation-app-name {
  color: #475569;
  font-size: 12px;
}

.conversation-app-title {
  color: #0f172a;
  font-weight: 600;
  line-height: 1.45;
}

.conversation-app-desc {
  color: #475569;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
}

.conversation-revoke {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-revoke-title {
  color: #7c2d12;
  font-weight: 600;
}

.conversation-revoke-sub {
  color: #6b7280;
  font-size: 12px;
}

.deal-probability-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.customer-tags-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.customer-tag-empty {
  color: #94a3b8;
}

.customer-name-link {
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  line-height: 1.4;
}

.customer-name-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.customer-name-link:disabled {
  color: #94a3b8;
  cursor: not-allowed;
  text-decoration: none;
}

.deal-probability-score {
  color: #475569;
  font-size: 12px;
}

.chat-meta {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 4px;
}

.ai-push-status {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  font-size: 10px;
  line-height: 16px;
  font-weight: 600;
}

.ai-push-status.is-success {
  color: #15803d;
  border-color: #86efac;
  background: #f0fdf4;
}

.ai-push-status.is-failed {
  color: #b91c1c;
  border-color: #fca5a5;
  background: #fef2f2;
}

.ai-push-status.is-pending {
  color: #334155;
  border-color: #cbd5e1;
  background: #f8fafc;
}

.loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-overlay::after {
  content: "";
  width: 32px;
  height: 32px;
  border: 3px solid rgba(15, 118, 110, 0.1);
  border-top-color: #0f766e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.panel-card {
  position: relative;
}

.full-height {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.pagination-container {
  margin-top: 12px;
  padding: 0 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.pagination-container .el-pagination {
  margin: 0;
}

.export-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.export-dialog-tip {
  font-size: 13px;
  color: #64748b;
}

.export-field-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 12px;
}

.export-field-grid :deep(.el-checkbox) {
  margin-right: 0;
}

@media (max-width: 900px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .filter-controls {
    width: 100%;
    display: grid;
    align-items: stretch;
    gap: 8px;
  }

  .export-field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-row {
    width: 100%;
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;
  }

  .filter-row-name {
    grid-template-columns: 64px minmax(0, 1fr) auto;
  }

  .filter-row-name .filter-query-btn {
    grid-column: 3 / 4;
    justify-self: end;
    white-space: nowrap;
  }

  .filter-row .name-filter,
  .filter-row .sales-filter,
  .filter-row .ai-filter,
  .filter-row .deal-filter {
    width: 100%;
  }

  .name-filter,
  .sales-filter,
  .ai-filter,
  .deal-filter {
    width: 100%;
  }

  .operation-cell {
    gap: 6px;
  }

  .chat-modal {
    padding: 10px;
  }

  .chat-card {
    width: 100%;
    max-height: 92vh;
    border-radius: 12px;
    padding: 10px;
  }

  .chat-head {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .chat-head h3 {
    line-height: 1.35;
    font-size: 20px;
    margin: 0;
    word-break: break-word;
  }

  .chat-head-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: 6px;
  }

  .chat-refresh-text {
    margin-right: auto;
    white-space: nowrap;
  }

  .chat-refresh-btn,
  .chat-head-actions .btn {
    flex-shrink: 0;
  }
}
</style>
